import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { sendBuyerInvitation } from "../services/emailService";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const APPLICATIONS = {
    PART1: {
      id: "PART1",
      name: "MISSING: The Search for Terry Hughes",
      shortName: "MISSING Part I"
    },
    PART2: {
      id: "PART2",
      name: "MISSING: The Final Secret",
      shortName: "MISSING Part II"
    }
  };

  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [directUserEmail, setDirectUserEmail] = useState("");
  const [selectedApplication, setSelectedApplication] = useState("PART1");
  const [pendingVerificationCount, setPendingVerificationCount] = useState(0);
  const [searchEmail, setSearchEmail] = useState("");
  const [newBuyerEmail, setNewBuyerEmail] = useState("");
  const [newLicenseCount, setNewLicenseCount] = useState("");

useEffect(() => {
  fetchPendingVerificationCount();  // <-- always fetch on mount

  const refreshNeeded = localStorage.getItem("refreshVerificationCount");
  if (refreshNeeded === "true") {
    localStorage.removeItem("refreshVerificationCount");
  }

  const interval = setInterval(fetchPendingVerificationCount, 60000);
  return () => clearInterval(interval);
}, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setError("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate, successMessage, error]);

  const apiRequest = async (url, method = "GET", body = null) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      throw new Error("No authentication token found");
    }

    const options = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    };

    if (body && method !== "GET") {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    if (response.status === 401) {
      localStorage.removeItem("token");
      navigate("/");
      throw new Error("Session expired. Please log in again.");
    }

    if (response.status === 404 && url.includes("/admin/license/")) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }

    return await response.json();
  };

  const fetchPendingVerificationCount = async () => {
    try {
      const response = await apiRequest("/admin/pending-verification-count");
      setPendingVerificationCount(response.count || 0);
    } catch (err) {
      console.error("Error fetching verification count:", err);
    }
  };

const handleLogout = () => {
  localStorage.removeItem("token");
  navigate("/");
};

const addBuyer = async () => {
  if (!newBuyerEmail || !newLicenseCount) {
    setError("Email and license count are required");
    return;
  }

  const appShortName = APPLICATIONS[selectedApplication].shortName;
  if (!window.confirm(
    `Add BUYER ${newBuyerEmail} with ${newLicenseCount} license(s) for ${appShortName}?\n\n` +
    `A buyer invitation email will be sent.`
  )) {
    return;
  }

  setLoading(true);
  setError("");
  setSuccessMessage("");

  try {
    const response = await apiRequest("/admin/assign-licenses", "POST", {
      buyerEmail: newBuyerEmail,
      totalLicenses: parseInt(newLicenseCount, 10),
      application: selectedApplication
    });

    const inviteToken = Math.random().toString(36).substring(2, 15) +
                        Math.random().toString(36).substring(2, 15);

    await apiRequest("/admin/store-invitation", "POST", {
      email: newBuyerEmail,
      token: inviteToken,
      userType: "Buyer",
      application: selectedApplication,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    });

    await sendBuyerInvitation(newBuyerEmail, inviteToken, selectedApplication);

    setNewBuyerEmail("");
    setNewLicenseCount("");
    setSuccessMessage(`Buyer ${newBuyerEmail} added with ${APPLICATIONS[selectedApplication].shortName} licenses and invitation sent successfully!`);

    await fetchBuyers();
  } catch (err) {
    console.error("Error adding buyer:", err);
    setError(`Failed to add buyer: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

const assignDirectUser = async () => {
  if (!directUserEmail) {
    setError("Please enter a user email address.");
    return;
  }

  const appShortName = APPLICATIONS[selectedApplication].shortName;
  if (!window.confirm(
    `Assign a DIRECT USER license for ${appShortName} to ${directUserEmail}?\n\n` +
    `(This is NOT a buyer. The user gets one license to play ${appShortName}.)`
  )) {
    return;
  }

  setLoading(true);
  setError("");
  setSuccessMessage("");

  try {
    const response = await apiRequest("/admin/assign-direct-user", "POST", {
      userEmail: directUserEmail,
      application: selectedApplication
    });

    // Show what the server actually did (new user + invitation, or added access for an existing user)
    setSuccessMessage(
      response && response.alreadyAssigned
        ? `${directUserEmail} already had a license. ${appShortName} access has been ADDED (no new invitation email is sent for existing users).`
        : `${appShortName} license directly assigned to ${directUserEmail}. Invitation email sent.`
    );
    setDirectUserEmail("");
    await fetchBuyers();
  } catch (err) {
    console.error("Error assigning direct user license:", err);
    setError(`Failed to assign direct user license: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

const fetchBuyers = async () => {
  setLoading(true);
  setError("");
  setSuccessMessage("");

  try {
    const data = await apiRequest("/admin/list-buyers");

    const processedData = Array.isArray(data) ? data.map(buyer => {
      if (!buyer.ApplicationLicenses) {
        buyer.ApplicationLicenses = {
          "PART1": buyer.TotalLicenses || 0
        };
      }

      if (buyer.Users && buyer.Users.length > 0) {
        buyer.Users = buyer.Users.map(user => {
          if (!user.applications) {
            return {
              ...user,
              applications: ["PART1"]
            };
          }
          return user;
        });
      }

      return buyer;
    }) : [];

    setBuyers(processedData);
  } catch (err) {
    console.error("Error fetching buyers:", err);
    setError(`Failed to fetch data: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

const searchBuyer = async () => {
  if (!searchEmail) return;

  setLoading(true);
  setError("");
  setSuccessMessage("");

  try {
    const data = await apiRequest(`/admin/license/${searchEmail}`);

    if (data) {
      if (!data.ApplicationLicenses) {
        data.ApplicationLicenses = {
          "PART1": data.TotalLicenses || 0
        };
      }

      if (data.Users && data.Users.length > 0) {
        data.Users = data.Users.map(user => {
          if (!user.applications) {
            return {
              ...user,
              applications: ["PART1"]
            };
          }
          return user;
        });
      }

      setBuyers([data]);
    } else {
      setBuyers([]);
    }
  } catch (err) {
    console.error("Error searching buyer:", err);
    setError(`Search failed: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

const resendInvitation = async (buyerEmail) => {
  setLoading(true);
  setError("");
  setSuccessMessage("");

  try {
    const inviteToken = Math.random().toString(36).substring(2, 15) +
                        Math.random().toString(36).substring(2, 15);

    await apiRequest("/admin/store-invitation", "POST", {
      email: buyerEmail,
      token: inviteToken,
      userType: "Buyer",
      application: selectedApplication,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    });

    await sendBuyerInvitation(buyerEmail, inviteToken, selectedApplication);
    setSuccessMessage(`${APPLICATIONS[selectedApplication].shortName} invitation resent to ${buyerEmail} successfully!`);
  } catch (err) {
    console.error("Error resending invitation:", err);
    setError(`Failed to resend invitation: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

const formatUserList = (users) => {
  if (!users || users.length === 0) return null;

  return users.map((user, index) => {
    const userApps = user.applications || ["PART1"];
    const hasAccess = userApps.includes(selectedApplication);

    const activatedApps = user.activatedApps || [];
    const isActivated = activatedApps.includes(selectedApplication);

    if (!hasAccess) return null;

    return (
      <li key={index}>
        {user.userEmail}
        {isActivated
          ? ` (${APPLICATIONS[selectedApplication].shortName} Activated)`
          : ` (${APPLICATIONS[selectedApplication].shortName} Not Activated)`}
      </li>
    );
  }).filter(Boolean);
};
 

return (
    <div style={{ padding: "1rem", maxWidth: 700, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h1 style={{ fontSize: "1.5rem", margin: 0 }}>Admin Dashboard</h1>
        
        <button 
          onClick={handleLogout}
          style={{ 
            padding: "0.5rem 1rem", 
            backgroundColor: "#f44336", 
            color: "white", 
            border: "none",
            borderRadius: "4px",
            cursor: "pointer" 
          }}
        >
          Logout
        </button>
      </div>
      
      {/* Academic Verification Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Academic Verification</h2>
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
          <button 
            onClick={() => navigate("/admin/academic-verification")}
            style={{ 
              padding: "0.5rem 1rem",
              backgroundColor: "#9C27B0",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              position: "relative"
            }}
          >
            Academic Verification Panel
            {pendingVerificationCount > 0 && (
              <span style={{
                position: "absolute",
                top: "-10px",
                right: "-10px",
                backgroundColor: "red",
                color: "white",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "bold"
              }}>
                {pendingVerificationCount}
              </span>
            )}
          </button>
        </div>
        <p style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.5rem" }}>
          Review and approve academic verification documents submitted by customers.
          {pendingVerificationCount > 0 && (
            <span style={{ color: "#f44336", fontWeight: "bold" }}> {pendingVerificationCount} pending!</span>
          )}
        </p>
      </div>
      
      {/* Application Selector */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Select Application</h2>
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
          {Object.keys(APPLICATIONS).map(appId => (
            <button
              key={appId}
              onClick={() => setSelectedApplication(appId)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: selectedApplication === appId ? "#4CAF50" : "#e0e0e0",
                color: selectedApplication === appId ? "white" : "black",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: selectedApplication === appId ? "bold" : "normal"
              }}
            >
              {APPLICATIONS[appId].shortName}
            </button>
          ))}
        </div>
      </div>
      
      {/* Success Message */}
      {successMessage && (
        <div style={{ 
          color: "green", 
          marginBottom: "1rem", 
          padding: "0.5rem", 
          backgroundColor: "#e8f5e9",
          borderRadius: "4px" 
        }}>
          {successMessage}
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div style={{ 
          color: "red", 
          marginBottom: "1rem", 
          padding: "0.5rem", 
          backgroundColor: "#ffebee",
          borderRadius: "4px" 
        }}>
          {error}
        </div>
      )}
      
      {/* Add Buyer Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Add Buyer for {APPLICATIONS[selectedApplication].shortName}</h2>
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
          <input
            placeholder="Buyer's email (purchaser who allocates licenses)"
            value={newBuyerEmail}
            onChange={(e) => setNewBuyerEmail(e.target.value)}
            style={{ 
              flex: 1, 
              padding: "0.5rem",
              borderRadius: "4px",
              border: "1px solid #ccc",
              minWidth: "200px"
            }}
          />
          <input
            placeholder="Total Licenses"
            type="number"
            value={newLicenseCount}
            onChange={(e) => setNewLicenseCount(e.target.value)}
            style={{ 
              width: "120px", 
              padding: "0.5rem",
              borderRadius: "4px",
              border: (newBuyerEmail && !newLicenseCount) ? "2px solid #d32f2f" : "1px solid #ccc"
            }}
          />
          <button 
            onClick={addBuyer} 
            disabled={loading || !newBuyerEmail || !newLicenseCount}
            style={{ 
              padding: "0.5rem 1rem",
              backgroundColor: (loading || !newBuyerEmail || !newLicenseCount) ? "#bdbdbd" : "#ff9800",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: (loading || !newBuyerEmail || !newLicenseCount) ? "not-allowed" : "pointer"
            }}
          >
            {loading ? "Adding..." : `Add Buyer (${selectedApplication === "PART2" ? "Part II" : "Part I"})`}
          </button>
        </div>
        {newBuyerEmail && !newLicenseCount && (
          <p style={{ fontSize: "0.9rem", color: "#d32f2f", marginTop: "0.5rem", marginBottom: 0, fontWeight: "bold" }}>
            Enter the number of licenses to add this buyer. (To give one person access to play, use Assign Direct License below.)
          </p>
        )}
        <p style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.5rem" }}>
          Note: Adding a buyer will automatically send an invitation email for {APPLICATIONS[selectedApplication].shortName}.
        </p>
      </div>
      
      {/* Direct User License Assignment Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Assign Direct {APPLICATIONS[selectedApplication].shortName} License</h2>
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
          <input
            placeholder="Student's email (person who will play)"
            value={directUserEmail}
            onChange={(e) => setDirectUserEmail(e.target.value)}
            style={{ 
              flex: 1, 
              padding: "0.5rem",
              borderRadius: "4px",
              border: "1px solid #ccc",
              minWidth: "200px"
            }}
          />
          <button 
            onClick={assignDirectUser} 
            disabled={loading || !directUserEmail}
            style={{ 
              padding: "0.5rem 1rem",
              backgroundColor: (loading || !directUserEmail) ? "#bdbdbd" : "#673ab7",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: (loading || !directUserEmail) ? "not-allowed" : "pointer"
            }}
          >
            {loading ? "Assigning..." : `Assign Direct ${selectedApplication === "PART2" ? "Part II" : "Part I"} License`}
          </button>
        </div>
        <p style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.5rem" }}>
          This will create a license for a user to play {APPLICATIONS[selectedApplication].shortName} directly, without requiring a separate buyer account.
        </p>
      </div>
      
      {/* Search Buyers Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Search Buyers</h2>
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
          <button 
            onClick={fetchBuyers} 
            disabled={loading}
            style={{ 
              padding: "0.5rem 1rem",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "default" : "pointer"
            }}
          >
            {loading ? "Loading..." : "Fetch All Buyers"}
          </button>
        </div>
        
        {/* Search Buyer By Email Section */}
        <h3 style={{ fontSize: "1rem", marginTop: "1rem" }}>Search Buyer By Email</h3>
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
          <input
            placeholder="Search buyer by email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            style={{ 
              flex: 1, 
              padding: "0.5rem",
              borderRadius: "4px",
              border: "1px solid #ccc"
            }}
          />
          <button 
            onClick={searchBuyer} 
            disabled={loading || !searchEmail}
            style={{ 
              padding: "0.5rem 1rem",
              backgroundColor: "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: (loading || !searchEmail) ? "default" : "pointer"
            }}
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>
      
      <div style={{ marginTop: "1rem" }}>
        <h2 style={{ fontSize: "1.2rem" }}>Buyers</h2>
        {loading ? (
          <p>Loading...</p>
        ) : buyers.length === 0 && searchEmail ? (
          <p>Not found</p>
        ) : buyers.length === 0 ? (
          <p>Please use Fetch All Buyers or Search Buyer by Email for updated listings</p>
        ) : (
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {buyers.map((buyer) => {
              // Get application-specific license count
              const appLicenses = buyer.ApplicationLicenses || {};
              const licenseCount = appLicenses[selectedApplication] || 0;
              
              // Count users with access to the selected application
              const usersWithApp = buyer.Users?.filter(user => {
                const userApps = user.applications || ["PART1"];
                return userApps.includes(selectedApplication);
              }).length || 0;
              
              // Count activated users for the selected application
              const activatedUsers = buyer.Users?.filter(user => {
                const userApps = user.applications || ["PART1"];
                const activatedApps = user.activatedApps || [];
                return userApps.includes(selectedApplication) && activatedApps.includes(selectedApplication);
              }).length || 0;
              
              return (
                <li 
                  key={buyer.BuyerEmail} 
                  style={{ 
                    marginBottom: "0.5rem", 
                    padding: "0.75rem", 
                    border: "1px solid #ddd",
                    borderRadius: "4px"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div><strong>Email:</strong> {buyer.BuyerEmail}</div>
                      <div><strong>{APPLICATIONS[selectedApplication].shortName} Licenses:</strong> {licenseCount}</div>
                      <div><strong>Used {APPLICATIONS[selectedApplication].shortName} Licenses:</strong> {activatedUsers}</div>
                      <div><strong>Status:</strong> {buyer.CognitoStatus || "Pending Registration"}</div>
                    </div>
                    <button
                      onClick={() => resendInvitation(buyer.BuyerEmail)}
                      disabled={loading || buyer.CognitoStatus === "Registered"}
                      style={{
                        padding: "0.5rem",
                        backgroundColor: buyer.CognitoStatus === "Registered" ? "#ccc" : "#2196F3",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        cursor: (loading || buyer.CognitoStatus === "Registered") ? "default" : "pointer"
                      }}
                    >
                      {buyer.CognitoStatus === "Registered" ? "Registered" : "Resend Invitation"}
                    </button>
                  </div>
                  
                  {buyer.Users && buyer.Users.length > 0 && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <strong>{APPLICATIONS[selectedApplication].shortName} Assigned Users:</strong>
                      <ul style={{ margin: "0.25rem 0 0 1rem" }}>
                        {formatUserList(buyer.Users)}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;